import { XIcon } from 'lucide-react';
import { useState, type KeyboardEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export function TagInput({
  value,
  onChange,
  suggestions = [],
  placeholder = 'Add a tag and press Enter',
  id,
  disabled,
}: {
  value: string[];
  onChange: (tags: string[]) => void;
  suggestions?: string[];
  placeholder?: string;
  id?: string;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState('');

  const add = (raw: string) => {
    const t = raw.trim().replace(/,+$/, '').trim();
    if (!t) return;
    if (!value.includes(t)) onChange([...value, t].sort((a, b) => a.localeCompare(b)));
    setDraft('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add(draft);
    } else if (e.key === 'Backspace' && !draft && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const remaining = suggestions.filter((s) => !value.includes(s) && (!draft || s.toLowerCase().includes(draft.toLowerCase()))).slice(0, 8);

  return (
    <div className="grid gap-2">
      <div className={cn('border-input dark:bg-input/30 flex min-h-9 flex-wrap items-center gap-1.5 border px-2 py-1.5 focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]', disabled && 'opacity-50')}>
        {value.map((t) => (
          <Badge key={t} variant="secondary" className="gap-1 pr-1">
            {t}
            <button type="button" aria-label={`Remove ${t}`} className="hover:text-destructive" onClick={() => onChange(value.filter((x) => x !== t))} disabled={disabled}>
              <XIcon className="size-3" />
            </button>
          </Badge>
        ))}
        <input
          id={id}
          className="placeholder:text-muted-foreground min-w-[8rem] flex-1 bg-transparent text-sm outline-none"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => add(draft)}
          placeholder={value.length === 0 ? placeholder : ''}
          disabled={disabled}
        />
      </div>
      {remaining.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {remaining.map((s) => (
            <button key={s} type="button" onClick={() => add(s)} className="border-input text-muted-foreground hover:bg-accent hover:text-accent-foreground border px-2 py-0.5 text-xs">
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
